<script lang="ts">
  /**
   * The map: OpenFreeMap vector tiles, the density surface, and the individual reports.
   *
   * Vector rather than raster is what lets our layers sit *below* the basemap's label layer,
   * so street names stay readable over the colouring instead of being covered by it
   * (brief §14). With raster tiles the only choices are all-over or all-under.
   *
   * Three ways to look at the same data, one at a time:
   *
   *   heat  a soft surface, for "where in the city is anything happening"
   *   hex   the H3 cells themselves, GDI-coloured, with the number from zoom 14 — and touching
   *         busy ones joined into swarms that pulse (amendment 14, `swarm-pulse.ts`)
   *   dots  individual reports, for confirming one
   *
   * Tilted, the map goes 3D (amendment 15): the buildings stand up in every view, and in the
   * hexagon view the cells rise into columns as tall as they are busy (`cell-columns.ts`).
   *
   * Zooming across 12, where the hexagons change size (brief §14), plays a big bang between the
   * two sizes (amendment 17, `cell-burst.ts`) — the small cells fuse into their parent, or the
   * parent tears apart into them.
   */
  import { centreOf } from '$domain/h3.ts';
  import { untrack } from 'svelte';
  import {
    AttributionControl,
    GeolocateControl,
    Map as MapLibreMap,
    type MapMouseEvent,
    Marker,
    NavigationControl,
  } from 'maplibre-gl';
  import 'maplibre-gl/dist/maplibre-gl.css';
  import { type CellBurst, createCellBurst } from './cell-burst.ts';
  import { type CellColumns, createCellColumns } from './cell-columns.ts';
  import { columnMetres, liftFor } from './cell-motion.ts';
  import { iconEmoji, labelEmojis } from './emoji-images.ts';
  import { gdiColourExpression, VIBE_SYMBOLS, vibeSymbol } from './gdi-scale.ts';
  // Side-effecting on purpose: it calls `setWorkerUrl` at module scope, which has to happen
  // before the first `new MapLibreMap`. Without it the worker asset 404s into the SPA fallback
  // and the whole map dies in silence — read the header of that file before touching this.
  import { collapseAttribution, workerIsUsable } from './maplibre.ts';
  import type { Rising } from './reactions.svelte.ts';
  import { createSwarmPulse, type SwarmPulse } from './swarm-pulse.ts';
  import type { CellCollection, Sighting } from './types.ts';

  export type MapLayer = 'heat' | 'hex' | 'dots';

  type Props = {
    styleUrlLight: string;
    styleUrlDark: string;
    attribution: string;
    sightings?: Sighting[];
    cells?: CellCollection | undefined;
    layer?: MapLayer;
    onpick?: (position: { lat: number; lon: number; accuracy: number }) => void;
    onselect?: (id: string) => void;
    oncell?: (cell: string) => void;
    onbounds?: (bbox: [number, number, number, number], zoom: number) => void;
    center?: [number, number];
    zoom?: number;
    /**
     * Where to move the map to, once it exists. Setting this pans; it never rebuilds the map,
     * which is why it is a separate prop from `center` rather than a change to it.
     */
    focus?: { lat: number; lon: number; zoom?: number } | undefined;
    /** Waben-Reaktionen to let rise from their cells, each once (amendment 24). */
    reactions?: Rising[];
  };

  const {
    styleUrlLight,
    styleUrlDark,
    attribution,
    sightings = [],
    cells,
    layer = 'hex',
    onpick,
    onselect,
    oncell,
    onbounds,
    // Stuttgart Marktplatz, the example the whole project was described with.
    center = [9.1789, 48.7767],
    zoom = 13,
    focus,
    reactions = [],
  }: Props = $props();

  let container: HTMLDivElement | undefined = $state();
  let map = $state<MapLibreMap | undefined>();
  let ready = $state(false);
  let failed = $state(false);
  // Whether the map is turned away from north. Drives the compass, which is hidden until it
  // has something to say — see the note on `.rotated` in the styles below.
  let rotated = $state(false);

  const POINTS = 'sightings';
  const HEXES = 'cells';
  const CENTRES = 'cell-centres';

  /** The hexagons' fill on a flat map. Tilted, they fade back under their columns. */
  const FLAT_FILL = 0.45;
  /**
   * The buildings stand up from 6° of tilt and are solid by 20°, a little ahead of the columns:
   * the city should be there before the cells rise out of it. Solid is also the cheap case —
   * MapLibre draws an extrusion that is not fully opaque in two passes.
   */
  const buildingsFor = (pitch: number) => Math.min(1, Math.max(0, (pitch - 6) / 14));

  /**
   * The font our own labels are set in: the basemap's, which OpenFreeMap serves.
   *
   * Without `text-font` MapLibre asks for its built-in default, "Open Sans Regular,Arial Unicode
   * MS Regular", and OpenFreeMap has no such stack. Every map load logged a 404 for it, and
   * MapLibre drew the numbers in whatever sans-serif the device had instead. Noto Sans Regular is
   * what both styles set their own labels in, and the only font the dark one uses at all.
   *
   * No font there has the emoji — OpenFreeMap serves nothing above U+FFFF — which is why the
   * flag and the vibe are icons (`emoji-images.ts`).
   */
  const LABEL_FONT = ['Noto Sans Regular'];

  /** The number on a hexagon. The vibe icon after it is drawn in the same colours. */
  const LABEL = { size: 13, color: '#ffffff', halo: 'rgba(0,0,0,0.6)', haloWidth: 1.2 } as const;

  /** A vibe symbol's icon is this and the emoji. */
  const VIBE_IMAGE = 'vibe-';

  /**
   * A space MapLibre keeps at the end of a label. It trims white space off the end, and a word
   * joiner (U+2060) after the space is none; nor does it take room, as OpenFreeMap's font has no
   * glyph for it.
   */
  const KEPT_SPACE = ` ${String.fromCharCode(0x2060)}`;

  /** The Wabenflagge: as big as 16 px text, and 1.15 of those above the cell's middle. */
  const FLAG = { size: 16, offset: [0, -1.15] } as const;

  // Made with the map and gone with it. Not `$state`: the effects that use them already re-run
  // on `map` and `ready`, which change at the same moments.
  let pulse: SwarmPulse | undefined;
  let columns: CellColumns | undefined;
  let burst: CellBurst | undefined;
  /** How far the hexagon layers may show: 1, except while a big bang stands in for them. */
  let reveal = 1;
  /** The cells the layers were last given, which the next big bang starts from. */
  let held: CellCollection | undefined;

  // `untrack` on the two that describe the *initial* view. The effect below builds the map,
  // and an effect re-runs when anything it read changes — so reading these tracked would mean
  // a new centre tears the map down and builds another one, losing the zoom, the layers and
  // whatever sheet was open. Moving the map is `focus`'s job, in its own effect.
  const initialCentre = () => untrack(() => center);
  const initialZoom = () => untrack(() => zoom);

  const prefersDark = () =>
    typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;

  const toPoints = (entries: Sighting[]) => ({
    type: 'FeatureCollection' as const,
    features: entries.map((entry) => ({
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [entry.lon, entry.lat] },
      properties: { id: entry.id, crowd: entry.crowd, mine: entry.mine },
    })),
  });

  const empty = { type: 'FeatureCollection' as const, features: [] };

  /**
   * The hexagons, with the vibe emoji and the column height folded into the properties.
   *
   * Both are derived rather than sent because they are rendering decisions — brief §9a.3 fixes
   * the vibe thresholds, and a style expression that reimplemented them would be a second copy
   * to forget about. The height is the one the columns are drawn at, for `cell-hit`.
   */
  const toHexes = (collection: CellCollection | undefined) => ({
    type: 'FeatureCollection' as const,
    features: (collection?.features ?? []).map((feature) => ({
      ...feature,
      properties: {
        ...feature.properties,
        vibeSymbol: vibeSymbol(feature.properties.vibe),
        height: columnMetres(feature.properties.cell, feature.properties.gdi),
      },
    })),
  });

  /**
   * The heatmap needs points; the API sends hexagons.
   *
   * The centre is computed from the cell index rather than from the polygon, because that is
   * the same answer the server would give and one line instead of a ring average.
   */
  const toCentres = (collection: CellCollection | undefined) => ({
    type: 'FeatureCollection' as const,
    features: (collection?.features ?? []).map((feature) => {
      const centre = centreOf(feature.properties.cell);
      return {
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [centre.lon, centre.lat] },
        properties: { gdi: feature.properties.gdi },
      };
    }),
  });

  const setData = (instance: MapLibreMap, id: string, data: unknown) => {
    const source = instance.getSource(id);
    if (source !== undefined && 'setData' in source) {
      (source as { setData: (value: unknown) => void }).setData(data);
    }
  };

  $effect(() => {
    if (container === undefined) return;

    // Read once, for all of them: the WebGL layers blend differently over the dark style than
    // the light one, and the buildings take their colour from it.
    const dark = prefersDark();
    const instance = new MapLibreMap({
      container,
      style: dark ? styleUrlDark : styleUrlLight,
      center: initialCentre(),
      zoom: initialZoom(),
      attributionControl: false,
    });
    map = instance;
    const swarmPulse = createSwarmPulse({ id: 'cell-pulse', dark });
    pulse = swarmPulse;
    const cellColumns = createCellColumns({ id: 'cell-columns', dark });
    columns = cellColumns;
    const cellBurst = createCellBurst({
      id: 'cell-burst',
      dark,
      onReveal: (share) => {
        reveal = share;
        restyle();
        // The swarms join cells, and while the cells are in flight there is nothing to join.
        if (share >= 1) swarmPulse.setCells(held);
      },
    });
    burst = cellBurst;

    // Bottom **left**, under the dock: the bottom right is the report button, and the
    // attribution — collapsed or not — was landing on top of it.
    instance.addControl(
      new AttributionControl({ compact: true, customAttribution: attribution }),
      'bottom-left',
    );
    // `compact: true` makes it collapsible, not collapsed. See maplibre.ts.
    collapseAttribution(instance);
    // The compass is built but hidden; CSS reveals it once the map is off north. Its click
    // handler is MapLibre's own `resetNorthPitch`, which is exactly the wanted behaviour and
    // not worth reimplementing — including the animation back.
    instance.addControl(
      new NavigationControl({ showCompass: true, visualizePitch: true }),
      'top-right',
    );
    instance.addControl(
      new GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
        // The accuracy circle stays on deliberately: it is how somebody understands why a
        // report can be refused as too imprecise (brief §6).
      }),
      'top-right',
    );

    const report = () => {
      const bounds = instance.getBounds();
      onbounds?.(
        [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
        instance.getZoom(),
      );
    };

    instance.on('load', () => {
      instance.addSource(POINTS, { type: 'geojson', data: empty });
      instance.addSource(HEXES, { type: 'geojson', data: empty });
      instance.addSource(CENTRES, { type: 'geojson', data: empty });

      // Insert below the first *street name*, so the names stay on top (brief §14) while the
      // roads and buildings they name sit under the colouring. "The first symbol layer", which
      // this used to be, is `water_name` in OpenFreeMap's dark style — ninth of forty-seven,
      // below every building and road. The hexagons came out cut into building-shaped pieces,
      // and a report's dot sat under the buildings too, where a house could hide it.
      //
      // `transportation_name` is the OpenMapTiles layer for road names, which every OpenFreeMap
      // style draws from; for a style without one, the first symbol layer is still the best
      // guess. Reading the layer list rather than hard-coding an id survives a style change.
      const styleLayers = instance.getStyle().layers ?? [];
      const firstLabel = (
        styleLayers.find(
          (entry) =>
            entry.type === 'symbol' &&
            (entry as { 'source-layer'?: string })['source-layer'] === 'transportation_name',
        ) ?? styleLayers.find((entry) => entry.type === 'symbol')
      )?.id;

      // The buildings, standing up once the map is tilted (amendment 15), from the same tiles
      // the flat ones come from. Laid out always and drawn only when tilted: MapLibre skips a
      // `fill-extrusion` at opacity 0 outright, whereas switching its visibility would make it
      // tile the whole basemap source again on every tilt. From zoom 14, where a building is
      // bigger than a pixel and the cost of thousands of them buys something.
      const flatBuildings = styleLayers.find(
        (entry) => (entry as { 'source-layer'?: string })['source-layer'] === 'building',
      );
      if (flatBuildings !== undefined && 'source' in flatBuildings) {
        instance.addLayer(
          {
            id: 'building-3d',
            type: 'fill-extrusion',
            source: flatBuildings.source,
            'source-layer': 'building',
            minzoom: 14,
            paint: {
              'fill-extrusion-color': dark ? '#2a2d34' : '#d8d5ce',
              'fill-extrusion-height': ['coalesce', ['get', 'render_height'], 6],
              'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
              'fill-extrusion-opacity': 0,
              'fill-extrusion-opacity-transition': { duration: 0 },
            },
          },
          firstLabel,
        );
      }

      instance.addLayer(
        {
          id: 'cell-heat',
          type: 'heatmap',
          source: CENTRES,
          paint: {
            'heatmap-weight': ['interpolate', ['linear'], ['get', 'gdi'], 0, 0, 100, 1],
            'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 10, 1, 16, 3],
            'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 10, 18, 16, 60],
            'heatmap-opacity': 0.75,
            // Transparent at zero so an empty area stays the basemap rather than a blue wash.
            'heatmap-color': [
              'interpolate',
              ['linear'],
              ['heatmap-density'],
              0,
              'rgba(0,0,0,0)',
              0.2,
              '#8b8f2f',
              0.4,
              '#d8a018',
              0.6,
              '#e8622a',
              0.8,
              '#ff2d55',
              1,
              '#ff2fd0',
            ],
          },
        },
        firstLabel,
      );

      instance.addLayer(
        {
          id: 'cell-fill',
          type: 'fill',
          source: HEXES,
          paint: {
            'fill-color': gdiColourExpression() as never,
            'fill-opacity': FLAT_FILL,
            // Set on every frame of a tilt (see `tilt`); a transition would trail behind it.
            'fill-opacity-transition': { duration: 0 },
          },
        },
        firstLabel,
      );

      instance.addLayer(
        {
          id: 'cell-outline',
          type: 'line',
          source: HEXES,
          paint: {
            'line-color': gdiColourExpression() as never,
            'line-width': 1,
            'line-opacity-transition': { duration: 0 },
          },
        },
        firstLabel,
      );

      // What a tap on a tilted map hits: the column, not the ground under it, which is
      // somewhere else on screen once the column stands. Never drawn — MapLibre skips an
      // extrusion at opacity 0 — but queried in 3D.
      instance.addLayer(
        {
          id: 'cell-hit',
          type: 'fill-extrusion',
          source: HEXES,
          paint: { 'fill-extrusion-height': ['get', 'height'], 'fill-extrusion-opacity': 0 },
        },
        firstLabel,
      );

      // The columns, after the buildings so that each hides what stands behind it; the big
      // bang's flying pieces among them; then the pulse, over the hexagons it joins and under
      // the street names like them.
      instance.addLayer(cellColumns.layer, firstLabel);
      instance.addLayer(cellBurst.layer, firstLabel);
      instance.addLayer(swarmPulse.layer, firstLabel);

      // The vibe after the number is the label's own icon, one per symbol, all made here and
      // alike in size (`labelEmojis`). Null only without a 2D canvas, which no browser drawing
      // this map lacks.
      const vibes = labelEmojis(Object.values(VIBE_SYMBOLS), LABEL_FONT, LABEL.size);
      for (const [symbol, made] of vibes?.images ?? []) {
        instance.addImage(VIBE_IMAGE + symbol, made.image, {
          sdf: true,
          pixelRatio: made.pixelRatio,
          stretchX: [[0, 1]],
        });
      }
      const vibeOffsets = [...(vibes?.textOffsets ?? [])].flatMap(([symbol, offset]) => [
        symbol,
        ['literal', offset],
      ]);

      instance.addLayer({
        id: 'cell-label',
        type: 'symbol',
        source: HEXES,
        // Brief §14: the number appears from zoom 14. Below that the hexagons are smaller
        // than the label and it becomes a field of overlapping digits.
        minzoom: 14,
        layout: {
          // With a vibe the number ends in a space, and the vibe's icon is stretched over the
          // number so that it lands right after it, where the emoji's glyph stood when it was
          // text (`labelEmojis`).
          'text-field': [
            'case',
            ['==', ['get', 'vibeSymbol'], ''],
            ['to-string', ['get', 'gdi']],
            ['concat', ['to-string', ['get', 'gdi']], KEPT_SPACE],
          ],
          'text-offset': (vibeOffsets.length === 0
            ? ['literal', [0, 0]]
            : ['match', ['get', 'vibeSymbol'], ...vibeOffsets, ['literal', [0, 0]]]) as never,
          'text-font': LABEL_FONT,
          'text-size': LABEL.size,
          'text-allow-overlap': false,
          'icon-image': [
            'case',
            ['==', ['get', 'vibeSymbol'], ''],
            '',
            ['concat', VIBE_IMAGE, ['get', 'vibeSymbol']],
          ],
          'icon-text-fit': 'width',
          'icon-text-fit-padding': vibes?.padding ?? [0, 0, 0, 0],
          'icon-offset': vibes?.iconOffset ?? [0, 0],
        },
        paint: {
          'text-color': LABEL.color,
          'text-halo-color': LABEL.halo,
          'text-halo-width': LABEL.haloWidth,
          'icon-color': LABEL.color,
          'icon-halo-color': LABEL.halo,
          'icon-halo-width': LABEL.haloWidth,
          // Held back while a big bang plays and faded in after it, frame by frame.
          'text-opacity-transition': { duration: 0 },
          'icon-opacity-transition': { duration: 0 },
        },
      });

      // Wabenflagge (amendment 13), as a layer of its own rather than a suffix on the number.
      // MapLibre drew the 🚩 glyph as a one-colour silhouette tinted by `text-color` — checked
      // against this very style on 2026-09-28 — so appended to the white label the flag would
      // have been a white one. Its own layer is what makes it red, and lets it appear from zoom
      // 12, where the report-resolution hexagons start, instead of waiting for the numbers at
      // 14. It is an SDF icon now, the same silhouette standing where the glyph stood
      // (`emoji-images.ts`); null only without a 2D canvas, which no browser drawing this lacks.
      const flag = iconEmoji('🚩', LABEL_FONT, FLAG.size, FLAG.offset);
      if (flag !== null) {
        instance.addImage('cell-flag', flag.image, { sdf: true, pixelRatio: flag.pixelRatio });
        instance.addLayer({
          id: 'cell-flag',
          type: 'symbol',
          source: HEXES,
          minzoom: 12,
          filter: ['has', 'flagHolder'],
          layout: {
            'icon-image': 'cell-flag',
            'icon-offset': flag.offset,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          },
          paint: {
            'icon-color': '#ff4d4f',
            'icon-halo-color': 'rgba(0,0,0,0.7)',
            'icon-halo-width': 1.2,
            // Tilted, the flag moves onto its column's tower (`cell-columns.ts`) and this one
            // fades out, frame by frame with the tilt.
            'icon-opacity-transition': { duration: 0 },
          },
        });
      }

      instance.addLayer(
        {
          id: 'sighting-glow',
          type: 'circle',
          source: POINTS,
          paint: {
            // Bigger for a bigger crowd — the one property a single report carries that a
            // reader can act on without the GDI.
            'circle-radius': ['interpolate', ['linear'], ['get', 'crowd'], 1, 10, 3, 20],
            'circle-color': '#ff2d55',
            'circle-blur': 0.8,
            'circle-opacity': 0.5,
          },
        },
        firstLabel,
      );

      instance.addLayer(
        {
          id: 'sighting-dot',
          type: 'circle',
          source: POINTS,
          paint: {
            'circle-radius': 5,
            'circle-color': ['case', ['get', 'mine'], '#ffd60a', '#ff2d55'],
            'circle-stroke-width': 1.5,
            'circle-stroke-color': '#ffffff',
          },
        },
        firstLabel,
      );

      // Tapping a report opens it (brief §9 — the confirm buttons live in that sheet). The
      // hit test is on the dot layer rather than the glow: the glow is deliberately much
      // bigger than the point it marks, and tapping the halo of one report while standing
      // over another would open the wrong one.
      instance.on('click', 'sighting-dot', (event) => {
        const id = event.features?.[0]?.properties?.id;
        if (typeof id === 'string') onselect?.(id);
      });
      // A cell is tapped where it shows: on the ground while the map is flat, on its column
      // once the columns stand. While they are still growing the ground is right for most of a
      // short column, and `cell-hit`, which knows only full heights, would answer for air.
      instance.on('click', (event) => {
        const layers = liftFor(instance.getPitch()) >= 1 ? ['cell-hit'] : ['cell-fill'];
        const cell = instance.queryRenderedFeatures(event.point, { layers })[0]?.properties?.cell;
        if (typeof cell === 'string') oncell?.(cell);
      });
      for (const hit of ['sighting-dot', 'cell-fill', 'cell-hit']) {
        instance.on('mouseenter', hit, () => {
          instance.getCanvas().style.cursor = 'pointer';
        });
        instance.on('mouseleave', hit, () => {
          instance.getCanvas().style.cursor = '';
        });
      }

      restyle();
      ready = true;
      report();
    });

    // A long press on a phone, a right-click on a desktop: pick a spot other than where you
    // are standing. Accuracy is reported as 0 because the point was chosen deliberately —
    // there is no GPS uncertainty in a tap.
    instance.on('contextmenu', (event: MapMouseEvent) => {
      onpick?.({ lat: event.lngLat.lat, lon: event.lngLat.lng, accuracy: 0 });
    });

    // This one *is* per frame, deliberately: it sets a boolean that shows or hides a button,
    // and waiting for `rotateend` would mean the compass appears only after the gesture is
    // over — by which time the person has stopped looking for it. Half a degree of dead zone
    // keeps a nudge during a two-finger zoom from flashing it on.
    const watchBearing = () => {
      rotated = Math.abs(instance.getBearing()) > 0.5 || instance.getPitch() > 0.5;
    };
    instance.on('rotate', watchBearing);
    instance.on('pitch', watchBearing);

    // Tilting is what makes the map 3D (amendment 15): the buildings fade in, and the flat
    // hexagons fade back as their columns rise over them. Constant paint values, so a frame of
    // a tilt costs a uniform and not a re-tiling — and only when the rounded value has moved.
    // The same goes for `reveal`, which holds the hexagons back while a big bang plays.
    const painted = new Map<string, number>();
    const paint = (
      id: string,
      property: Parameters<MapLibreMap['setPaintProperty']>[1],
      value: number,
    ) => {
      const rounded = Math.round(value * 100) / 100;
      const key = `${id}/${property}`;
      if (painted.get(key) === rounded || instance.getLayer(id) === undefined) return;
      painted.set(key, rounded);
      instance.setPaintProperty(id, property, rounded);
    };
    const restyle = () => {
      const pitch = instance.getPitch();
      const lift = liftFor(pitch);
      paint('cell-fill', 'fill-opacity', FLAT_FILL * (1 - 0.65 * lift) * reveal);
      paint('cell-outline', 'line-opacity', (1 - 0.65 * lift) * reveal);
      paint('cell-label', 'text-opacity', reveal);
      paint('cell-label', 'icon-opacity', reveal);
      paint('cell-flag', 'icon-opacity', (1 - lift) * reveal);
      paint('building-3d', 'fill-extrusion-opacity', buildingsFor(pitch));
      cellColumns.setReveal(reveal);
    };
    instance.on('pitch', restyle);

    // `moveend` and not `move`: refetching on every animation frame of a pan would be a
    // request per frame, and the endpoint has a per-minute limit for exactly that reason.
    instance.on('moveend', report);

    instance.on('error', (event) => {
      failed = true;
      console.error('map error', event.error);
    });

    // The one failure MapLibre will not report. If the worker asset is not a module, the map
    // never draws, never errors and never times out — so ask directly and say so, rather than
    // leaving a black rectangle and an empty console.
    void workerIsUsable().then((usable) => {
      if (usable) return;
      failed = true;
      console.error('map worker asset is not a module - see web/src/lib/maplibre.ts');
    });

    return () => {
      swarmPulse.destroy();
      pulse = undefined;
      cellColumns.destroy();
      columns = undefined;
      cellBurst.destroy();
      burst = undefined;
      reveal = 1;
      held = undefined;
      instance.remove();
      map = undefined;
      ready = false;
    };
  });

  // Move the map when the parent asks. `easeTo` and not `jumpTo`: the map is already showing
  // somewhere, and a picture that slides is one somebody can follow — a picture that teleports
  // leaves them working out what just happened.
  $effect(() => {
    const instance = map;
    const target = focus;
    if (instance === undefined || target === undefined) return;
    instance.easeTo({
      center: [target.lon, target.lat],
      zoom: target.zoom ?? untrack(() => instance.getZoom()),
      duration: 900,
    });
  });

  // Re-feed the sources whenever the data changes. Updating an existing source rather than
  // removing and re-adding the layer keeps the map from flickering on every poll.
  $effect(() => {
    const instance = map;
    if (instance === undefined || !ready) return;
    setData(instance, POINTS, toPoints(sightings));
    setData(instance, HEXES, toHexes(cells));
    setData(instance, CENTRES, toCentres(cells));
    columns?.setCells(cells);

    // A new size of hexagon is a zoom across 12 (brief §14): play the big bang from the old
    // cells to the new ones (amendment 17). The layers already hold the new cells — taps land
    // on what will be there — and stay hidden until the play hands over to them.
    const before = held;
    held = cells;
    const switched =
      before !== undefined && cells !== undefined && before.resolution !== cells.resolution;
    if (switched && untrack(() => layer) === 'hex' && burst?.play(before, cells) === true) {
      pulse?.setCells(undefined);
      return;
    }
    // Anything else shows as it is, including an answer that overtakes a play still running.
    burst?.stop();
    pulse?.setCells(cells);
  });

  // Waben-Reaktionen (amendment 24): each rises once from the middle of its cell and is gone.
  // A marker, not a layer: MapLibre keeps it on its cell through every pan and zoom, and it
  // asks the map for no frames — the browser moves it. The marker's own element carries
  // MapLibre's transform, so the rising is done by the emoji inside it.
  const risen = new Set<string>();
  const RISE_MS = 2600;
  $effect(() => {
    const instance = map;
    const waiting = reactions;
    if (instance === undefined || !ready) return;
    for (const entry of waiting) {
      if (risen.has(entry.id)) continue;
      risen.add(entry.id);
      const centre = centreOf(entry.cell);
      const element = document.createElement('div');
      element.className = 'reaction-rise';
      const emoji = document.createElement('span');
      emoji.textContent = entry.emoji;
      // Two thrown at once should not stack into one: a little drift and delay apart.
      emoji.style.setProperty('--drift', `${Math.round(Math.random() * 32 - 16)}px`);
      emoji.style.setProperty('--delay', `${Math.round(Math.random() * 300)}ms`);
      element.append(emoji);
      const marker = new Marker({ element, anchor: 'bottom' })
        .setLngLat([centre.lon, centre.lat])
        .addTo(instance);
      setTimeout(() => marker.remove(), RISE_MS + 400);
    }
  });

  // One layer at a time. Toggling visibility rather than adding and removing layers means
  // switching back is instant and the paint properties survive.
  $effect(() => {
    const instance = map;
    if (instance === undefined || !ready) return;
    const shown: Record<MapLayer, string[]> = {
      heat: ['cell-heat'],
      hex: [
        'cell-fill',
        'cell-outline',
        'cell-hit',
        'cell-columns',
        'cell-burst',
        'cell-pulse',
        'cell-label',
        'cell-flag',
      ],
      dots: ['sighting-glow', 'sighting-dot'],
    };
    for (const [name, ids] of Object.entries(shown)) {
      for (const id of ids) {
        if (instance.getLayer(id) === undefined) continue;
        instance.setLayoutProperty(id, 'visibility', name === layer ? 'visible' : 'none');
      }
    }
    // Hidden is not enough for the pulse or the flags: they would go on asking for frames.
    pulse?.setEnabled(layer === 'hex');
    columns?.setEnabled(layer === 'hex');
    if (layer !== 'hex') burst?.stop();
  });
</script>

<div class="map" class:rotated bind:this={container} aria-label="Karte"></div>

{#if failed}
  <p class="map-error" role="status">
    Die Karte konnte nicht geladen werden. Ohne Netz ist das normal — sonst lohnt ein Blick in
    die Konsole.
  </p>
{/if}

<style>
  .map {
    position: absolute;
    inset: 0;
  }

  /*
   * The compass appears when there is a north to go back to, and not before.
   *
   * A permanently visible compass on a map that is almost always north-up is a button that
   * only ever means "nothing to do here". `display: none` rather than `opacity` so the
   * control group really is two buttons tall until the map is turned — a reserved gap would
   * be the same clutter with the ink removed.
   *
   * `:global` because MapLibre builds these elements itself and Svelte's scoping never
   * reaches them; the `.map` prefix keeps it to this component's map all the same.
   */
  .map :global(.maplibregl-ctrl-compass) {
    display: none;
  }

  .map.rotated :global(.maplibregl-ctrl-compass) {
    display: block;
  }

  /* A reaction rising from its cell: up, a little sideways, larger, and gone. */
  .map :global(.reaction-rise) {
    pointer-events: none;
  }

  .map :global(.reaction-rise span) {
    display: block;
    font-size: 1.7rem;
    line-height: 1;
    filter: drop-shadow(0 2px 3px rgb(0 0 0 / 45%));
    animation: -global-reaction-rise 2.6s cubic-bezier(0.2, 0.7, 0.3, 1) var(--delay) both;
  }

  @keyframes -global-reaction-rise {
    0% {
      opacity: 0;
      transform: translate(0, 10px) scale(0.4);
    }
    15% {
      opacity: 1;
      transform: translate(calc(var(--drift) * 0.3), -10px) scale(1.15);
    }
    100% {
      opacity: 0;
      transform: translate(var(--drift), -90px) scale(1.4);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .map :global(.reaction-rise span) {
      animation: -global-reaction-still 2.6s ease-out both;
    }

    @keyframes -global-reaction-still {
      0%,
      70% {
        opacity: 1;
      }
      100% {
        opacity: 0;
      }
    }
  }

  .map-error {
    position: absolute;
    left: 50%;
    bottom: 5rem;
    transform: translateX(-50%);
    margin: 0;
    max-width: min(32rem, calc(100% - 2rem));
    padding: 0.75rem 1rem;
    border: 1px solid var(--border);
    border-radius: 0.75rem;
    background: var(--card);
    color: var(--muted);
    font-size: 0.875rem;
  }
</style>
